/**
 * CSV Formula Injection sanitizer (AVR-1004).
 *
 * Prefixes dangerous cell values (=, +, -, @) with a leading single-quote so
 * spreadsheet apps treat them as text rather than formulas. Spreadsheet apps
 * trim leading whitespace before evaluating a cell, so the check looks at the
 * first *non-whitespace* character rather than the raw first character —
 * otherwise a payload like " =cmd|calc" or "\n=cmd|calc" would still be
 * evaluated as a formula. A value that is pure whitespace (e.g. a lone TAB)
 * has no such character and is left untouched.
 *
 * Numeric strings (e.g. "-1234.56") pass through untouched — Number() rejects
 * any embedded non-numeric payload, so a disguised string like "-1)+calc" still
 * fails the check and gets escaped.
 *
 * Phone/fax columns may start with '+' (international dialling prefix) and are
 * exempt from that specific escape when the key is listed in `phoneLikeFields`.
 */

const FORMULA_PREFIXES = ['=', '+', '-', '@'];

export const DEFAULT_PHONE_LIKE_FIELDS = [
    'telephone', 'fax', 'represTel', 'represFax', 'fixEstTel', 'fixEstFax', 'represTelephone'
];

/**
 * Sanitize a single CSV cell value.
 * Non-string values and pure numeric strings are returned as-is.
 *
 * @param {*} value
 * @param {boolean} [allowLeadingPlus=false] - skip escaping a leading '+'.
 * @returns {*}
 */
export function sanitizeCsvValue(value, allowLeadingPlus = false) {
    if (typeof value !== 'string' || value.length === 0) {
        return value;
    }

    // Numeric strings (e.g. "-1234.56") are safe amounts, not formula payloads.
    // Number() rejects embedded non-numeric chars, so "-1)+calc" still gets escaped.
    if (value.trim() !== '' && !isNaN(Number(value))) {
        return value;
    }

    // Check the first non-whitespace character, not the raw first character —
    // spreadsheet apps trim leading whitespace before evaluating a cell, so a
    // payload could otherwise hide a formula prefix behind a leading space/LF/etc.
    const firstChar = value.replace(/^\s+/, '').charAt(0);
    if (allowLeadingPlus && firstChar === '+') {
        return value;
    }

    if (FORMULA_PREFIXES.indexOf(firstChar) !== -1) {
        return "'" + value;
    }
    return value;
}

/**
 * Sanitize a react-csv `data` array (row-objects or row-arrays).
 * Non-array inputs are returned unchanged.
 *
 * @param {Array} data
 * @param {string[]} [phoneLikeFields] - keys allowed to start with '+'.
 * @returns {Array}
 */
export function sanitizeCsvData(data, phoneLikeFields = DEFAULT_PHONE_LIKE_FIELDS) {
    if (!Array.isArray(data)) {
        return data;
    }

    return data.map((row) => {
        if (Array.isArray(row)) {
            return row.map((value) => sanitizeCsvValue(value));
        }

        if (row && typeof row === 'object') {
            const sanitizedRow = {};
            Object.keys(row).forEach((key) => {
                const allowLeadingPlus = phoneLikeFields.indexOf(key) !== -1;
                sanitizedRow[key] = sanitizeCsvValue(row[key], allowLeadingPlus);
            });
            return sanitizedRow;
        }

        return sanitizeCsvValue(row);
    });
}

export default sanitizeCsvData;
